/**
 * toggleHeader.ts - Pure mutation functions for toggling a table's header row / column
 */

import produce from 'immer';

import {type Table} from 'notebook/tables/constants/TableConstants';
import {type TableMutationResult} from 'notebook/tables/excess/ops/TableOpConstants';

/**
 * Toggle the header column flag on a table.
 *
 * Flips `table.headerCol` and clears any bold formatting on the cells in the first
 * column (the column that becomes / stops being the header). Returns a `noop` table op
 * since toggling the header does not require spreadsheet evaluation.
 *
 * @param table - The current table
 * @returns The mutation result with the new table and a `noop` operation
 */
export function toggleHeaderCol(table: Table): TableMutationResult {
  const newTable = produce(table, (draftTable) => {
    draftTable.headerCol = !draftTable.headerCol;
    // Reset bold of the first column when toggling the header column
    draftTable.table.forEach((draftRow) => {
      const firstCell = draftRow[0];
      if (firstCell != null) {
        delete firstCell.bold;
      }
    });
  });

  return {
    table: newTable,
    tableOp: {type: 'noop'},
    affectedCells: [],
  };
}

/**
 * Toggle the header row flag on a table.
 *
 * Flips `table.headerRow` and clears any bold formatting on the cells in the first
 * row (the row that becomes / stops being the header). Returns a `noop` table op
 * since toggling the header does not require spreadsheet evaluation.
 *
 * @param table - The current table
 * @returns The mutation result with the new table and a `noop` operation
 */
export function toggleHeaderRow(table: Table): TableMutationResult {
  const newTable = produce(table, (draftTable) => {
    draftTable.headerRow = !draftTable.headerRow;
    // Reset bold of the first row when toggling the header row. Guard against empty
    // tables (the Table type permits `table: []`).
    draftTable.table[0]?.forEach((cell) => {
      delete cell.bold;
    });
  });

  return {
    table: newTable,
    tableOp: {type: 'noop'},
    affectedCells: [],
  };
}
